import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { FileText, ChevronRight, Plus, Trash2, Loader2 } from 'lucide-react';
import { Breadcrumbs } from '@/components/layout/Breadcrumbs';
import { Markdown } from '@/components/common/Markdown';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  useSubsection,
  useUpdateSubsection,
  useDeleteSubsection,
  useCreateArticle,
} from '@/features/content/queries';
import { useAuth } from '@/features/auth/AuthProvider';
import { Editor } from '@/features/editor/Editor';
import { Comments } from '@/features/comments/Comments';
import { EditToolbar } from '@/features/content/EditToolbar';

interface Draft {
  titulo: string;
  descripcion: string;
  contentMd: string;
}

function SubsectionPage() {
  const { section: sectionSlug, subsection: subsectionSlug } = Route.useParams();
  const navigate = useNavigate();
  const { data, isLoading } = useSubsection(sectionSlug, subsectionSlug);
  const { user } = useAuth();
  const update = useUpdateSubsection();
  const remove = useDeleteSubsection();
  const createArticle = useCreateArticle();

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Draft>({ titulo: '', descripcion: '', contentMd: '' });
  const [newArticleOpen, setNewArticleOpen] = useState(false);
  const [newArticleTitle, setNewArticleTitle] = useState('');

  useEffect(() => {
    if (data?.subsection) {
      setDraft({
        titulo: data.subsection.titulo,
        descripcion: data.subsection.descripcion ?? '',
        contentMd: data.subsection.contentMd,
      });
    }
  }, [data?.subsection]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-6 w-1/3" />
        <Skeleton className="h-10 w-1/2" />
        <Skeleton className="h-40" />
      </div>
    );
  }
  if (!data) return <div>Subsección no encontrada</div>;
  const { section, subsection } = data;
  const isAdmin = user?.rol === 'admin';

  const save = async () => {
    await update.mutateAsync({
      id: subsection.id,
      data: {
        titulo: draft.titulo,
        descripcion: draft.descripcion,
        contentMd: draft.contentMd,
      },
    });
    setEditing(false);
  };

  const cancel = () => {
    setDraft({
      titulo: subsection.titulo,
      descripcion: subsection.descripcion ?? '',
      contentMd: subsection.contentMd,
    });
    setEditing(false);
  };

  const del = async () => {
    if (
      !confirm(
        `¿Eliminar la subsección "${subsection.titulo}" y todos sus artículos? Esta acción no se puede deshacer.`,
      )
    )
      return;
    await remove.mutateAsync(subsection.id);
    navigate({ to: '/seccion/$section', params: { section: section.slug } });
  };

  const createArt = async () => {
    if (!newArticleTitle.trim()) return;
    const res = await createArticle.mutateAsync({
      subsectionId: subsection.id,
      titulo: newArticleTitle.trim(),
      contentMd: '',
    });
    setNewArticleOpen(false);
    setNewArticleTitle('');
    navigate({
      to: '/seccion/$section/$subsection/$article',
      params: {
        section: section.slug,
        subsection: subsection.slug,
        article: res.article.slug,
      },
    });
  };

  return (
    <div className="space-y-6">
      <Breadcrumbs
        items={[
          { label: section.titulo, to: `/seccion/${section.slug}` },
          { label: subsection.titulo },
        ]}
      />

      {isAdmin && (
        <EditToolbar
          editing={editing}
          onEdit={() => setEditing(true)}
          onSave={save}
          onCancel={cancel}
          saving={update.isPending}
          viewActions={
            <>
              <Button size="sm" variant="brand" onClick={() => setNewArticleOpen(true)}>
                <Plus className="mr-1.5 h-3.5 w-3.5" />
                Nuevo artículo
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive"
                onClick={del}
              >
                <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                Eliminar
              </Button>
            </>
          }
          editActions={
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive ml-auto"
              onClick={del}
            >
              <Trash2 className="mr-1.5 h-3.5 w-3.5" />
              Eliminar
            </Button>
          }
        />
      )}

      <header className="border-b pb-6">
        {editing ? (
          <>
            <Input
              value={draft.titulo}
              onChange={(e) => setDraft({ ...draft, titulo: e.target.value })}
              placeholder="Título de la subsección"
              className="h-auto border-0 bg-transparent px-0 text-3xl font-bold tracking-tight shadow-none focus-visible:ring-0"
            />
            <Textarea
              value={draft.descripcion}
              onChange={(e) => setDraft({ ...draft, descripcion: e.target.value })}
              placeholder="Descripción breve"
              rows={2}
              className="mt-2 resize-none border-0 px-0 text-base shadow-none focus-visible:ring-0"
            />
          </>
        ) : (
          <>
            <h1 className="text-3xl font-bold tracking-tight">{subsection.titulo}</h1>
            {subsection.descripcion && (
              <p className="text-muted-foreground mt-2 max-w-2xl">{subsection.descripcion}</p>
            )}
          </>
        )}
      </header>

      {editing ? (
        <Editor
          value={draft.contentMd}
          onChange={(md) => setDraft({ ...draft, contentMd: md })}
          placeholder="Contenido propio de la subsección…"
        />
      ) : subsection.contentMd ? (
        <Markdown content={subsection.contentMd} />
      ) : (
        <div className="bg-muted/30 text-muted-foreground rounded-lg border p-6 text-sm">
          Esta subsección aún no tiene contenido propio.{' '}
          {isAdmin && 'Usa “Editar página” para agregarlo.'}
        </div>
      )}

      {subsection.articles.length > 0 && (
        <section className="pt-4">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Artículos en esta subsección
          </h2>
          <Card className="divide-y">
            {subsection.articles.map((a) => (
              <Link
                key={a.id}
                to="/seccion/$section/$subsection/$article"
                params={{
                  section: section.slug,
                  subsection: subsection.slug,
                  article: a.slug,
                }}
                className="group hover:bg-accent/40 flex items-center gap-3 px-4 py-3 transition-colors first:rounded-t-xl last:rounded-b-xl"
              >
                <FileText className="text-muted-foreground h-4 w-4" />
                <span className="flex-1 truncate text-sm font-medium">{a.titulo}</span>
                <ChevronRight className="text-muted-foreground h-4 w-4 opacity-0 transition-opacity group-hover:opacity-100" />
              </Link>
            ))}
          </Card>
        </section>
      )}

      <Comments subsectionId={subsection.id} />

      <Dialog open={newArticleOpen} onOpenChange={setNewArticleOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Nuevo artículo</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium">Título</label>
            <Input
              autoFocus
              value={newArticleTitle}
              onChange={(e) => setNewArticleTitle(e.target.value)}
              placeholder="Ej. Flujo de devoluciones"
              onKeyDown={(e) => {
                if (e.key === 'Enter') createArt();
              }}
            />
            <p className="text-muted-foreground text-xs">
              Se creará en la subsección “{subsection.titulo}”. Podrás editar el contenido luego.
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setNewArticleOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={createArt}
              disabled={createArticle.isPending || !newArticleTitle.trim()}
            >
              {createArticle.isPending && (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              )}
              Crear y abrir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export const Route = createFileRoute('/seccion/$section_/$subsection')({
  component: SubsectionPage,
});
